import { PerformanceAudienceFilterOption } from '@/services/endpoints/performanceAudience';
import { Popover, Transition } from '@headlessui/react';
import { Fragment, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { getRealAccountOptions } from '../../utils/buildCampaignPerformanceDashboardArgs';
import { MetaIcon } from '../PlatformBrandIcons';
import {
  paChipClass,
  paChipDisabledClass,
  paChipOpenClass,
  paPopBtnClass,
  paPopClass,
  paPopNodeClass,
  paPopNodeSelectedClass,
} from '../PerformanceAudienceComponents';
import { FilterDisabledTooltip } from '../FilterDisabledTooltip';

type MetaScopeMultiSelectProps = {
  items: PerformanceAudienceFilterOption[];
  selectedIds: string[];
  onApply: (ids: string[]) => void;
  disabled?: boolean;
  className?: string;
};

export const MetaScopeMultiSelect: FC<MetaScopeMultiSelectProps> = ({ items, selectedIds, onApply, disabled, className }) => {
  const { t } = useTranslation('performanceAudience');
  const accounts = useMemo(() => getRealAccountOptions(items), [items]);
  const [draftIds, setDraftIds] = useState<string[]>(selectedIds);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setDraftIds(selectedIds);
  }, [selectedIds]);

  const filteredAccounts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return accounts;
    }

    return accounts.filter((account) => {
      const name = account.value.toLowerCase();
      const id = account.id.toLowerCase();
      return name.includes(query) || id.includes(query);
    });
  }, [accounts, search]);

  const chipValue = useMemo(() => {
    if (!selectedIds.length) {
      return t('metaMulti.noneSelected');
    }

    if (selectedIds.length === 1) {
      return accounts.find((account) => account.id === selectedIds[0])?.value ?? selectedIds[0];
    }

    if (accounts.length > 0 && selectedIds.length >= accounts.length) {
      return t('metaMulti.allAdAccounts');
    }

    return t('metaMulti.countAdAccounts', { count: selectedIds.length });
  }, [accounts, selectedIds, t]);

  const totalCount = accounts.length;
  const accountCountSuffix = totalCount ? `· ${totalCount} ad account` : undefined;

  const toggleId = (id: string) => {
    setDraftIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const isDisabled = disabled || !accounts.length;

  return (
    <FilterDisabledTooltip disabled={isDisabled} source='meta'>
      <Popover className={twMerge('relative', className)} as='div'>
      {({ open, close }) => (
        <div className={twMerge(paChipClass, open && paChipOpenClass, isDisabled && paChipDisabledClass)}>
          <Popover.Button
            disabled={isDisabled}
            className='flex w-full items-center gap-2 border-0 bg-transparent p-0 text-left outline-none'
            onClick={() => {
              setDraftIds(selectedIds);
              setSearch('');
            }}
          >
            <span className='shrink-0'>
              <MetaIcon />
            </span>
            <span className='text-ink-500'>{t('filters.meta')}</span>
            <span className='truncate font-semibold text-ink-900'>{chipValue}</span>
            {accountCountSuffix ? <span className='text-[11.5px] font-normal text-ink-400'>{accountCountSuffix}</span> : null}
            <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden>
              <path d='M6 9l6 6 6-6' />
            </svg>
          </Popover.Button>

          <Transition
            show={open}
            as={Fragment}
            enter='transition ease-in duration-100'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition ease-in duration-150'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
            afterLeave={() => {
              setDraftIds(selectedIds);
              setSearch('');
            }}
          >
            <Popover.Panel static className={twMerge(paPopClass, 'w-[420px]')}>
              <div className='relative border-b border-ink-100 px-3.5 pb-2.5 pt-3'>
                <svg
                  className='pointer-events-none absolute left-6 top-1/2 size-3.5 -translate-y-1/2 text-ink-400'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.7'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden
                >
                  <circle cx='11' cy='11' r='6' />
                  <path d='m20 20-3.5-3.5' />
                </svg>
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t('metaMulti.searchPlaceholder')}
                  aria-label={t('metaMulti.searchPlaceholder')}
                  className='w-full rounded-lg border border-ink-200 bg-ink-50 py-2 pl-8 pr-2.5 text-[13px] text-ink-900 outline-none focus:border-ar-blue focus:bg-white'
                />
              </div>

              <div className='max-h-[380px] overflow-y-auto pb-1.5 pt-0'>
                {filteredAccounts.length === 0 ? (
                  <div className={twMerge(paPopNodeClass, 'cursor-default text-ink-500')}>{t('metaMulti.noResults')}</div>
                ) : (
                  filteredAccounts.map((account) => {
                    const isSelected = draftIds.includes(account.id);
                    return (
                      <div
                        key={account.id}
                        role='option'
                        aria-selected={isSelected}
                        className={twMerge(paPopNodeClass, isSelected && paPopNodeSelectedClass)}
                        onClick={() => toggleId(account.id)}
                      >
                        <span
                          className={twMerge(
                            'inline-flex size-3.5 shrink-0 items-center justify-center rounded border-[1.5px] border-ink-300 text-[9px] text-white',
                            isSelected && 'border-ar-blue bg-ar-blue'
                          )}
                        >
                          {isSelected ? '✓' : ''}
                        </span>
                        <div className='min-w-0 flex-1'>
                          <div className={twMerge('font-medium text-ink-900', isSelected && 'font-semibold text-ar-blue-dark')}>{account.value}</div>
                          <div className='font-mono text-[11px] text-ink-400'>{account.id}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className='flex items-center justify-between gap-2.5 border-t border-ink-100 px-3.5 py-2.5 text-[12.5px] text-ink-600'>
                <span>
                  {t('metaMulti.selectedCount', {
                    selected: draftIds.length,
                    total: totalCount,
                  })}
                </span>
                <button
                  type='button'
                  className={paPopBtnClass}
                  disabled={draftIds.length === 0}
                  onClick={() => {
                    onApply(draftIds);
                    close();
                  }}
                >
                  {t('metaMulti.apply')}
                </button>
              </div>
            </Popover.Panel>
          </Transition>
        </div>
      )}
    </Popover>
    </FilterDisabledTooltip>
  );
};
